import { useCallback, useState } from 'react';

import { useMonthCells } from '../calendar/MonthCellsProvider';
import { applyWorkLogs } from './api';
import type { ApplyWorkLogEntryCommand, PendingEntry } from './models';
import { usePending } from './PendingProvider';

const toWireEntry = (p: PendingEntry): ApplyWorkLogEntryCommand =>
    p.action === 'remove'
        ? { action: 'Remove', workItemId: p.workItemId, date: p.date, hours: p.hours }
        : { action: 'Add', workItemId: p.workItemId, date: p.date, hours: p.hours, note: null };

export const useApplyPending = (): {
    apply: () => Promise<boolean>;
    applying: boolean;
    error: string | null;
} => {
    const { pending, clear } = usePending();
    const { apply: applyToCells } = useMonthCells();

    const [applying, setApplying] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const apply = useCallback(async (): Promise<boolean> => {
        setApplying(true);
        setError(null);
        try {
            await applyWorkLogs({ entries: pending.map((p) => toWireEntry(p)) });
            applyToCells(pending);
            clear();
            return true;
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Apply failed.');
            return false;
        } finally {
            setApplying(false);
        }
    }, [pending, applyToCells, clear]);

    return { apply, applying, error };
};
